import InfoBlock from '@mortgage-pos/ui/components/InfoBlock'
import { Form, Formik } from 'formik'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import FormQuestion from '@mortgage-pos/ui/components/FormQuestion'
import React, { FC, useEffect, useState } from 'react'
import { Page as PageType, Routes } from '@mortgage-pos/types'
import NavigationButtons from '@mortgage-pos/ui/components/NavigationButtons'
import { useNavigate } from 'react-router-dom'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import { toCurrency, parseCamelCase } from '@mortgage-pos/ui/utils/stringFormat'
import Button from '@mortgage-pos/ui/components/Button'
import { ListContent } from '@mortgage-pos/ui/components/ListContent'
import PhoneAppVerifyDetails, {
  PhoneAppVerifyDetailsVariant,
} from '@mortgage-pos/ui/pages/PhoneAppVerifyDetails'
import parse from 'html-react-parser'
import RobinhoodBanner from './RobinhoodBanner'

import {
  useInitialValues,
  useValidate,
} from '@mortgage-pos/ui/utils/formikHelpers'

import {
  getCustomValidator,
  calculateSumOfFields,
  interpolateAnswersInPage,
  deriveAnswersFromFormValues,
} from '@mortgage-pos/ui/utils/pageHelpers'
import AmazonDemoHeader from './AmazonDemoHeader'

type Props = {
  page: PageType
  answer: Function
  mergedAnswers: object
}

const SUBMIT_WAIT_TIME = process.env.APP_ENV === 'test' ? 0 : 500

export const Page: FC<Props> = ({ page: rawPage, answer }) => {
  const navigate = useNavigate()
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)

  const { answers, mergedAnswers, maxCashoutValue } = useStoreState(
    (state) => state.answers
  )

  const maxCashoutValueCurrency = toCurrency(maxCashoutValue)

  const { isAutoCompletedAddress } = useStoreState((state) => state.address)
  const { loanOfficer } = useStoreState((state) => state.loanOfficer)
  const prebuiltValidations = useStoreState(
    (state) => state.visibleIfConditions
  )
  const {
    isAmazonBenefitsFlow,
    isStart,
    isRobinhoodGoldFlow,
    isRobinhoodGoldMember,
  } = useStoreState((state) => state.questionnaire)
  const { autoCompleteAddressAnswers } = useStoreActions(
    (state) => state.address
  )
  const { next } = useStoreActions((state) => state.questionnaire)
  const { formErrored } = useStoreActions((state) => state.tracking)
  const { getLoanOfficer } = useStoreActions((state) => state.loanOfficer)

  const checkErrorScroll = () => {
    const element = document.querySelector('.--error')
    // Need to test for scrollIntoView (unit tests)
    if (element && element.scrollIntoView) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (hasValidationErrors) {
      checkErrorScroll()
    }
  }, [hasValidationErrors])

  useEffect(() => {
    if (
      mergedAnswers.hasOwnProperty.call(mergedAnswers, 'phone') &&
      mergedAnswers.hasOwnProperty.call(mergedAnswers, 'firstName') &&
      mergedAnswers.hasOwnProperty.call(mergedAnswers, 'lastName') &&
      mergedAnswers.hasOwnProperty.call(mergedAnswers, 'state') &&
      !loanOfficer
    ) {
      getLoanOfficer()
    }
  }, [])

  const page = interpolateAnswersInPage(rawPage, {
    ...mergedAnswers,
    maxCashoutValueCurrency,
  })
  const {
    name,
    title,
    section,
    subTitle,
    questions,
    description,
    modifierClass,
    displaySumOfFields,
    customPageValidation,
    validationErrorMsg,
  } = page

  const validateCustomPageCondition = getCustomValidator(
    customPageValidation,
    validationErrorMsg
  )

  const hasHeadingGroup = title || subTitle || description

  useEffect(() => {
    newRelic.increment('page_view', [
      { key: 'name', value: name },
      { key: 'section', value: section },
    ])
  }, [])

  const onSubmitHandler = async (values, formikUtils) => {
    setDisableSubmit(true)
    const errors = await validateCustomPageCondition(
      mergedAnswers,
      values,
      formikUtils
    )
    if (errors) {
      checkErrorScroll()
    }

    const noCustomErrors =
      errors && Object.values(errors).every((err) => err.length <= 0)

    if (noCustomErrors) {
      const allAnswers = { ...mergedAnswers, ...values }

      let answers: any = deriveAnswersFromFormValues(
        questions,
        allAnswers,
        prebuiltValidations
      )

      const pageHasAutoCompleteAddress = questions.find(
        (question) => question.questionName.name === 'autoCompleteAddress'
      )

      // Always attempt to auto-fill address values
      if (pageHasAutoCompleteAddress) {
        await autoCompleteAddressAnswers(answers)
      }

      // Don't answer the auto-complete question if not an exact match
      if (pageHasAutoCompleteAddress && !isAutoCompletedAddress) {
        await answer({ ...answers, autoCompleteAddress: null })
        await next()
        return
      }

      const pageHasAutoCompleteAddressFields = questions.find(
        (question) => question.questionName.name === 'autoCompleteAddressFields'
      )

      if (pageHasAutoCompleteAddressFields) {
        answers = { ...answers, ...values }

        // Auto complete address can be used in a question group
        let addresses = []
        Object.entries(values).forEach(([property, value]: [string, any[]]) => {
          if (property.startsWith('addresses+')) {
            // If the property starts with addresses+ then it's an array of addresses
            addresses = [...addresses, ...value]
          }
        })

        if (answers?.addresses?.length > 0 || addresses.length > 0) {
          answers.addresses = [
            ...(answers?.addresses || []),
            ...addresses,
          ].filter((address) => address.street)
        }

        // Auto complete co-borrower address can be used in a question group
        let coBorrowerAddresses = []
        Object.entries(values).forEach(([property, value]: [string, any[]]) => {
          if (property.startsWith('coBorrowerAddresses+')) {
            // If the property starts with coBorrowerAddresses+ then it's an array of addresses
            coBorrowerAddresses = [...coBorrowerAddresses, ...value]
          }
        })

        if (
          answers?.coBorrowerAddresses?.length > 0 ||
          coBorrowerAddresses.length > 0
        ) {
          answers.coBorrowerAddresses = [
            ...(answers?.coBorrowerAddresses || []),
            ...coBorrowerAddresses,
          ]
            .filter((address) => address.street)
            .map(({ autoCompleteAddressFields, ...address }) => {
              // This removes the autoCompleteAddressFields from the address
              // object, since the coBorrowerAddresses objects are not setup
              // to accept this field
              return {
                ...address,
              }
            })
        }
      }

      answers = { ...answers, ...values }

      try {
        await answer(answers)
        await next()
      } catch (err) {
        const errorKey =
          err.name === 'internallyThrownError'
            ? 'internallyThrownError'
            : 'page'

        const error = {
          [errorKey]: err.message,
        }

        formikUtils.setStatus(error)
      }
    } else {
      formErrored({
        errorMessage: JSON.stringify(errors),
        errorType: 'FormSubmissionError',
        errorDetails: `Alfie Section: ${section}`,
      })

      formikUtils.setStatus(errors)
    }
    setTimeout(() => setDisableSubmit(false), SUBMIT_WAIT_TIME)
  }

  // Trigger form reload whenever answers changes. Hack for Formik's
  // re-render limitation: https://github.com/formium/formik/issues/811
  const [formikRenderKey, setFormikRenderKey] = useState(0)

  useEffect(() => {
    const pageHasApiLoader = rawPage?.questions?.some(
      (question) => question.component === 'Question-APICallLoader'
    )

    // Don't re-render on pricing page or apiLoader to avoid unnecessary extra calls
    if (rawPage?.name !== 'pricing' && !pageHasApiLoader) {
      setFormikRenderKey(formikRenderKey + 1)
    }
  }, [answers])

  const initialValues = useInitialValues(questions)
  const validate = useValidate(questions)

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmitHandler}
      data-testid="questionnaire-page"
      key={`formik-${formikRenderKey}`}
    >
      {({ errors, values, submitCount, isValid, status, ...rest }) => {
        // Using status instead of Errors b/c Formik is wiping out errors w/ validate or validateSchema
        const { page: pageLevelError, internallyThrownError } = status || {}

        if (submitCount) {
          // Ignore page errors
          const errorKeys = Object.keys(errors).filter((key) => key !== 'page')
          if (errorKeys.length && !hasValidationErrors) {
            formErrored({
              errorMessage: JSON.stringify(errors),
              errorType: 'ValidationError',
              errorDetails: `Alfie Section: ${section}`,
            })
            checkErrorScroll()
            setHasValidationErrors(true)
          }
          if (!errorKeys.length && hasValidationErrors) {
            setHasValidationErrors(false)
          }

          // This is used to route users to the welcome page if they exceed the max number of identity verification attempts
          if (
            submitCount > 3 &&
            page?.name === 'confirmPhone' &&
            pageLevelError
          ) {
            navigate(`/${Routes.APP_1003}/thank-you`)
          }
        }

        return (
          <div data-testid={`page-content-${name}`}>
            {isAmazonBenefitsFlow ? <AmazonDemoHeader /> : null}
            <Form>
              {isStart && isRobinhoodGoldFlow && isRobinhoodGoldMember && (
                <RobinhoodBanner showViewDisclosures={true} />
              )}
              {hasHeadingGroup ? (
                <hgroup className="page__heading-group">
                  {title ? <h1 className="page__title">{title}</h1> : null}
                  {subTitle ? (
                    <h2 className="page__subtitle">{subTitle}</h2>
                  ) : null}
                  {description ? (
                    <p className="page__description">{parse(description)}</p>
                  ) : null}
                </hgroup>
              ) : null}

              {name === 'cashOutEquity' || name === 'cashOutNonEquity' ? (
                <ListContent />
              ) : null}

              {
                //Will be removed after Compare POC
                page.name === 'compare-success' ? (
                  <Button
                    className="file-upload__button"
                    content="Download Fee Breakdown Letter"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      window.open(
                        '@mortgage-pos/ui/assets/bankrate/Compare_POC_Fee_Estimate_Robin.pdf',
                        '_blank'
                      )
                    }}
                  />
                ) : null
              }
              {
                //Will be removed after Compare POC
                page.name === 'compare-prequal-success' ? (
                  <Button
                    className="file-upload__button"
                    content="Download Prequal Letter"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      window.open(
                        '@mortgage-pos/ui/assets/bankrate/robinTestcoPrequal.pdf',
                        '_blank'
                      )
                    }}
                  />
                ) : null
              }

              {page.name === 'verifyDetails' ? (
                <PhoneAppVerifyDetails
                  variant={PhoneAppVerifyDetailsVariant.Primary}
                />
              ) : null}

              {page.name === 'verifyDetailsCoBorrower' ? (
                <PhoneAppVerifyDetails
                  variant={PhoneAppVerifyDetailsVariant.CoBorrower}
                />
              ) : null}

              {questions.map((question) => (
                <FormQuestion
                  key={question.questionName.name}
                  question={question}
                  values={values}
                />
              ))}

              {displaySumOfFields ? (
                <div className="page__sum-of-fields">
                  <span className="page__sum-of-fields-title">
                    Total {parseCamelCase(name)}:
                  </span>
                  <span className="page__sum-of-fields-amount">
                    {toCurrency(calculateSumOfFields(values), { decimals: 2 })}
                  </span>
                </div>
              ) : null}

              {pageLevelError || internallyThrownError ? (
                <InfoBlock
                  type="danger"
                  setAsHtmlError={!!internallyThrownError}
                  children={pageLevelError || internallyThrownError}
                ></InfoBlock>
              ) : null}
              <NavigationButtons
                modifierClass={modifierClass}
                // Since this is in a form, this button will first try and submit the form
                // And additionally call the checkErrorScroll function to scroll to a form error if there is one
                onClick={checkErrorScroll}
                disabled={disableSubmit}
              />
            </Form>
          </div>
        )
      }}
    </Formik>
  )
}
